/**
 * Platform singleton — the ONLY place the player app touches auth/events/flags
 * plumbing. Everything is driven through @matchday/client factories; no route
 * ever constructs a transport or reads localStorage directly.
 *
 * - tracker: createTracker → HTTP transport → VITE_INGEST_URL (default
 *   http://localhost:8789). pagehide flush rides navigator.sendBeacon via
 *   drainForBeacon(); a refused beacon requeues.
 * - auth: createAuthClient → VITE_MESSAGING_URL (default http://localhost:8788)
 *   with a localStorage SessionStore + the stable device hash.
 * - flags: createFlagsClient with an optional snapshot URL (VITE_FLAGS_URL)
 *   and a manual feed fallback — the app runs with no backend at all.
 * - session_started fires exactly once per boot with source
 *   deeplink | share | organic derived from the entry location.
 * - screen_viewed fires on every route change (App wires trackScreen into the
 *   wouter location effect).
 */
import {
  createAuthClient,
  createFlagsClient,
  createHttpTransport,
  createManualFlagFeed,
  createTracker,
  type SessionStore,
} from "@matchday/client";
import { AuthSession, SessionId } from "@matchday/contracts";

// ── Env (defaults let the app boot with zero backend) ────────────────────────

export const INGEST_URL = import.meta.env.VITE_INGEST_URL ?? "http://localhost:8789";
export const MESSAGING_URL = import.meta.env.VITE_MESSAGING_URL ?? "http://localhost:8788";
const FLAGS_URL = import.meta.env.VITE_FLAGS_URL;

// ── Device hash: stable random id in localStorage ────────────────────────────

const DEVICE_HASH_KEY = "matchday.device_hash";

function loadOrMintDeviceHash(): string {
  try {
    const existing = localStorage.getItem(DEVICE_HASH_KEY);
    if (existing !== null && existing.length > 0) return existing;
    const minted = crypto.randomUUID();
    localStorage.setItem(DEVICE_HASH_KEY, minted);
    return minted;
  } catch {
    // private mode etc. — a per-boot hash still satisfies the envelope shape
    return crypto.randomUUID();
  }
}

export const deviceHash: string = loadOrMintDeviceHash();

// ── App session (ClickHouse `sessions` spine: one id per boot) ───────────────

export const bootSessionId: SessionId = SessionId.parse(crypto.randomUUID());

/** deeplink (/m/…) | share (/share-relay) | organic — from the entry URL. */
export function classifySource(pathname: string): "deeplink" | "share" | "organic" {
  if (pathname.startsWith("/m/")) return "deeplink";
  if (pathname.startsWith("/share-relay")) return "share";
  return "organic";
}

// ── Tracker ──────────────────────────────────────────────────────────────────

export const tracker = createTracker({
  transport: createHttpTransport({ baseUrl: INGEST_URL }),
  sessionId: bootSessionId,
  deviceHash,
  // Transport failures are expected offline behaviour — the queue holds.
  onError: () => undefined,
});

// pagehide: drain the queue into a beacon; requeue if the beacon is refused.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    const events = tracker.drainForBeacon();
    if (events.length === 0) return;
    const ok = navigator.sendBeacon(
      `${INGEST_URL}/ingest`,
      new Blob([JSON.stringify(events)], { type: "application/json" }),
    );
    if (!ok) tracker.requeue(events);
  });
}

// ── Auth (localStorage-backed SessionStore) ──────────────────────────────────

const AUTH_SESSION_KEY = "matchday.auth_session";

class LocalStorageSessionStore implements SessionStore {
  async load(): Promise<AuthSession | null> {
    try {
      const raw = localStorage.getItem(AUTH_SESSION_KEY);
      if (raw === null) return null;
      const parsed = AuthSession.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  async save(session: AuthSession): Promise<void> {
    try {
      localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    } catch {
      // private mode — session lives for the tab only
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.removeItem(AUTH_SESSION_KEY);
    } catch {
      // ignore
    }
  }
}

export const auth = createAuthClient({
  baseUrl: MESSAGING_URL,
  storage: new LocalStorageSessionStore(),
  deviceHash,
});

// ── Flags (snapshot URL when configured; manual feed always available) ───────

export const flagFeed = createManualFlagFeed();

export const flags = createFlagsClient({
  ...(FLAGS_URL !== undefined ? { baseUrl: FLAGS_URL } : {}),
  subscribe: flagFeed.subscribe,
});
flags.connect();
// Snapshot fetch is best-effort: no backend, no problem — the app runs on the
// manual feed (empty flag cache ⇒ nothing is killed, features default off).
void flags.load().catch(() => undefined);

// ── Boot events ──────────────────────────────────────────────────────────────

let sessionStartEmitted = false;

/**
 * Emit session_started exactly once per boot. player_id comes from a stored
 * (still-valid) auth session when one exists; the 90-day slide is re-anchored
 * on the same touch (KICKOFF §2.3 / MATCH POINT §3.1).
 */
export async function emitSessionStarted(pathname: string): Promise<void> {
  if (sessionStartEmitted) return;
  sessionStartEmitted = true;
  const session = await auth.touchSession();
  tracker.track("session_started", {
    player_id: session?.player_id ?? null,
    session_id: bootSessionId,
    source: classifySource(pathname),
  });
}

/** screen_viewed on every route change — App owns the location effect. */
export function trackScreen(route: string): void {
  tracker.track("screen_viewed", { session_id: bootSessionId, route });
}
